import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/templates/layout/AdminLayout';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    Button,
    Slider,
    FormControl,
    FormLabel
} from '@mui/material';
import {
    Storage as StorageIcon,
    People as PeopleIcon,
    Article as ArticleIcon,
    LocalOffer as TagIcon,
    Folder as FolderIcon,
    Email as EmailIcon,
    TrendingUp as TrendingUpIcon,
    Speed as SpeedIcon,
    Memory as MemoryIcon,
    Settings as SettingsIcon,
    AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { useTheme } from '@mui/material/styles';
import { systemAPI } from '../../services/systemAPI';
import ScrapingSpeedTest from '../../components/templates/admin/ScrapingSpeedTest';
import MensureLoader from '../../components/atoms/MensureLoader';

const SystemDashboardPage = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [storage, setStorage] = useState(null);
    const [articlesStats, setArticlesStats] = useState(null);
    const [aiUsage, setAIUsage] = useState(null);

    // Scraping config state
    const [scrapingConfig, setScrapingConfig] = useState(null);
    const [scrapingConfigLoading, setScrapingConfigLoading] = useState(false);
    const [scrapingConfigSaving, setScrapingConfigSaving] = useState(false);
    const [scrapingConfigSuccess, setScrapingConfigSuccess] = useState(false);

    useEffect(() => {
        fetchStats();
        fetchScrapingConfig();
        fetchAIUsage();
    }, []);

    const fetchScrapingConfig = async () => {
        try {
            const response = await systemAPI.getScrapingConfig();
            if (response.success) {
                setScrapingConfig(response.data);
            }
        } catch (err) {
            console.error('Erro ao carregar config de scraping:', err);
        }
    };

    const fetchAIUsage = async () => {
        try {
            const response = await api.get('/admin/ai-usage/summary', { params: { days: 30 } });
            if (response.data?.success) {
                setAIUsage(response.data.data);
            }
        } catch (err) {
            if (err.response?.status !== 403) {
                console.error('Erro ao carregar uso de IA:', err);
            }
        }
    };

    const handleSaveScrapingConfig = async () => {
        try {
            setScrapingConfigSaving(true);
            await systemAPI.updateScrapingConfig(scrapingConfig);
            setScrapingConfigSuccess(true);
            setTimeout(() => setScrapingConfigSuccess(false), 3000);
        } catch (err) {
            console.error('Erro ao salvar config de scraping:', err);
        } finally {
            setScrapingConfigSaving(false);
        }
    };

    const handleSpeedTestResult = (bestLevel) => {
        setScrapingConfig(prev => ({ ...prev, parallelLimit: bestLevel }));
        handleSaveScrapingConfig();
    };

    const fetchStats = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const [statsRes, storageRes, articlesRes] = await Promise.all([
                api.get('/system/stats'),
                api.get('/system/storage'),
                api.get('/system/articles-stats')
            ]);

            setStats(statsRes.data.data);
            setStorage(storageRes.data.data);
            setArticlesStats(articlesRes.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao carregar estatísticas');
            console.error('Erro ao buscar estatísticas:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <Box
                    sx={{
                        minHeight: 'calc(100vh - 64px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <MensureLoader size="lg" message="Carregando dashboard do sistema..." />
                </Box>
            </AdminLayout>
        );
    }

    if (error) {
        return (
            <AdminLayout>
                <Container maxWidth="lg" sx={{ py: 4 }}>
                    <Alert severity="error">{error}</Alert>
                </Container>
            </AdminLayout>
        );
    }

    const StatCard = ({ title, value, icon: Icon, color = 'primary', subtitle }) => (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Icon sx={{ fontSize: 32, color: `${color}.main`, mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">{title}</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: `${color}.main` }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
                )}
            </CardContent>
        </Card>
    );

    const formatNumber = (value) => Number(value || 0).toLocaleString('pt-BR');
    const formatBRL = (value) => Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    });
    const aiScopeMap = Object.fromEntries((aiUsage?.byScope || []).map(item => [item._id, item]));
    const clientAIUsage = aiScopeMap.client || {};
    const operationalAIUsage = aiScopeMap.operational || {};
    const unknownAIUsage = aiScopeMap.unknown || {};

    return (
        <AdminLayout>
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Dashboard do Sistema
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Monitoramento de uso e estatísticas da plataforma
                </Typography>
            </Box>

            {/* Contadores Gerais */}
            <Typography variant="h6" sx={{ mb: 2 }}>📊 Visão Geral</Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title="Usuários"
                        value={stats?.counts?.users?.toLocaleString() || 0}
                        icon={PeopleIcon}
                        color="primary"
                        subtitle={`${stats?.counts?.activeUsers30d || 0} ativos (30d)`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title="Marcas"
                        value={stats?.counts?.brands?.toLocaleString() || 0}
                        icon={StorageIcon}
                        color="secondary"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title="Artigos"
                        value={stats?.counts?.articles?.toLocaleString() || 0}
                        icon={ArticleIcon}
                        color="success"
                        subtitle={`${stats?.growth?.avgPerDay30d || 0}/dia avg`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title="Tags"
                        value={stats?.counts?.tags?.toLocaleString() || 0}
                        icon={TagIcon}
                        color="warning"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title="Coleções"
                        value={stats?.counts?.collections?.toLocaleString() || 0}
                        icon={FolderIcon}
                        color="info"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                        title="Newsletters"
                        value={stats?.counts?.newsletters?.toLocaleString() || 0}
                        icon={EmailIcon}
                        color="error"
                    />
                </Grid>
            </Grid>

            {aiUsage && (
                <>
                    <Typography variant="h6" sx={{ mb: 2 }}>Uso de IA nos ultimos 30 dias</Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={4}>
                            <StatCard
                                title="Chamadas IA"
                                value={formatNumber(aiUsage?.totals?.calls)}
                                icon={AutoAwesomeIcon}
                                color="info"
                                subtitle={`${formatNumber(aiUsage?.totals?.totalTokens)} tokens | ${formatBRL(aiUsage?.totals?.estimatedCostBRL)} est.`}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <StatCard
                                title="Clientes"
                                value={formatNumber(clientAIUsage.totalTokens)}
                                icon={PeopleIcon}
                                color="success"
                                subtitle={`${formatNumber(clientAIUsage.calls)} chamadas | ${formatBRL(clientAIUsage.estimatedCostBRL)} est.`}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <StatCard
                                title="Operacional"
                                value={formatNumber(operationalAIUsage.totalTokens)}
                                icon={SettingsIcon}
                                color="warning"
                                subtitle={`${formatNumber(operationalAIUsage.calls)} chamadas | ${formatBRL(operationalAIUsage.estimatedCostBRL)} est.`}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, height: '100%' }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>IA por servico</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Servico</TableCell>
                                                <TableCell align="right">Chamadas</TableCell>
                                                <TableCell align="right">Tokens</TableCell>
                                                <TableCell align="right">Custo est.</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(aiUsage.byService || []).slice(0, 6).map(item => (
                                                <TableRow key={item._id || 'unspecified'}>
                                                    <TableCell>{item._id || 'unspecified'}</TableCell>
                                                    <TableCell align="right">{formatNumber(item.calls)}</TableCell>
                                                    <TableCell align="right">{formatNumber(item.totalTokens)}</TableCell>
                                                    <TableCell align="right">{formatBRL(item.estimatedCostBRL)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, height: '100%' }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>IA por cliente</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Cliente</TableCell>
                                                <TableCell align="right">Chamadas</TableCell>
                                                <TableCell align="right">Tokens</TableCell>
                                                <TableCell align="right">Custo est.</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(aiUsage.byClient || []).slice(0, 6).map(item => (
                                                <TableRow key={item._id || item.clientEmail || 'unknown'}>
                                                    <TableCell>{item.clientEmail || 'Sem cliente vinculado'}</TableCell>
                                                    <TableCell align="right">{formatNumber(item.calls)}</TableCell>
                                                    <TableCell align="right">{formatNumber(item.totalTokens)}</TableCell>
                                                    <TableCell align="right">{formatBRL(item.estimatedCostBRL)}</TableCell>
                                                </TableRow>
                                            ))}
                                            {unknownAIUsage.calls > 0 && (
                                                <TableRow>
                                                    <TableCell>Sem classificacao</TableCell>
                                                    <TableCell align="right">{formatNumber(unknownAIUsage.calls)}</TableCell>
                                                    <TableCell align="right">{formatNumber(unknownAIUsage.totalTokens)}</TableCell>
                                                    <TableCell align="right">{formatBRL(unknownAIUsage.estimatedCostBRL)}</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Crescimento e MongoDB */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Crescimento de Artigos */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                            <TrendingUpIcon sx={{ mr: 1 }} /> Crescimento de Artigos
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">Últimos 7 dias</Typography>
                                <Typography variant="h5" color="success.main">
                                    {stats?.growth?.last7Days?.toLocaleString() || 0}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">Últimos 30 dias</Typography>
                                <Typography variant="h5" color="success.main">
                                    {stats?.growth?.last30Days?.toLocaleString() || 0}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">Últimos 90 dias</Typography>
                                <Typography variant="h5" color="success.main">
                                    {stats?.growth?.last90Days?.toLocaleString() || 0}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* MongoDB Status */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                            <MemoryIcon sx={{ mr: 1 }} /> MongoDB Atlas
                        </Typography>
                        {stats?.mongodb?.error ? (
                            <Alert severity="warning">{stats.mongodb.error}</Alert>
                        ) : (
                            <>
                                {/* Barra de uso */}
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">
                                            {stats?.mongodb?.usedMB} MB usados de {stats?.mongodb?.planSizeMB} MB
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: parseFloat(stats?.mongodb?.usagePercent) > 80 ? 'error.main' : 'success.main' }}>
                                            {stats?.mongodb?.usagePercent}%
                                        </Typography>
                                    </Box>
                                    <Box sx={{ 
                                        width: '100%', 
                                        height: 20, 
                                        bgcolor: 'grey.200', 
                                        borderRadius: 1,
                                        overflow: 'hidden'
                                    }}>
                                        <Box sx={{ 
                                            width: `${Math.min(parseFloat(stats?.mongodb?.usagePercent) || 0, 100)}%`, 
                                            height: '100%', 
                                            bgcolor: parseFloat(stats?.mongodb?.usagePercent) > 80 ? 'error.main' : 'success.main',
                                            transition: 'width 0.3s'
                                        }} />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        {stats?.mongodb?.remainingMB} MB disponível
                                    </Typography>
                                </Box>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">Plano</Typography>
                                        <Typography variant="body1">{stats?.mongodb?.plan}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">Collections</Typography>
                                        <Typography variant="body1">{stats?.mongodb?.collectionCount || 0}</Typography>
                                    </Grid>
                                </Grid>
                                
                                <Box sx={{ mt: 2 }}>
                                    <a 
                                        href="https://cloud.mongodb.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ color: '#1976d2', textDecoration: 'underline', fontSize: '14px' }}
                                    >
                                        Ver detalhes no Atlas →
                                    </a>
                                </Box>
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Storage */}
            <Typography variant="h6" sx={{ mb: 2 }}>💾 Armazenamento</Typography>
            <Paper sx={{ p: 3, mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Total:</strong> {storage?.total?.sizeMB} MB ({storage?.total?.sizeGB} GB)
                    </Typography>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Collection</TableCell>
                                <TableCell align="right">Documentos</TableCell>
                                <TableCell align="right">Tamanho</TableCell>
                                <TableCell align="right">Storage</TableCell>
                                <TableCell align="right">Média/Doc</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(storage?.collections || {}).map(([name, data]) => (
                                <TableRow key={name}>
                                    <TableCell component="th" scope="row">
                                        <Chip label={name} size="small" />
                                    </TableCell>
                                    <TableCell align="right">{data.documents?.toLocaleString() || 'N/A'}</TableCell>
                                    <TableCell align="right">{data.sizeMB || 'N/A'} MB</TableCell>
                                    <TableCell align="right">{data.storageMB || 'N/A'} MB</TableCell>
                                    <TableCell align="right">{data.avgDocSize || 'N/A'} KB</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Artigos por Mídia */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>📱 Artigos por Mídia</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Tipo</TableCell>
                                        <TableCell align="right">Quantidade</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {articlesStats?.byMediaType?.map(item => (
                                        <TableRow key={item._id}>
                                            <TableCell>{item._id || 'N/A'}</TableCell>
                                            <TableCell align="right">{item.count.toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>😊 Artigos por Sentimento</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Sentimento</TableCell>
                                        <TableCell align="right">Quantidade</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {articlesStats?.bySentiment?.map(item => (
                                        <TableRow key={item._id}>
                                            <TableCell>
                                                <Chip
                                                    label={item._id}
                                                    size="small"
                                                    color={
                                                        item._id === 'positive' ? 'success' :
                                                        item._id === 'negative' ? 'error' : 'default'
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell align="right">{item.count.toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Ranking Usuários */}
            <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>👥 10+ Usuários (por artigos)</Typography>
            <Paper sx={{ p: 3, mb: 4 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell align="right">Artigos</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stats?.topUsers?.map((user, idx) => (
                                <TableRow key={user._id}>
                                    <TableCell>{idx + 1}</TableCell>
                                    <TableCell>{user.name}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                        <Chip label={user.role} size="small" />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip label={user.articleCount} color="primary" size="small" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Ranking Marcas */}
            <Typography variant="h6" sx={{ mb: 2 }}>🏢 10+ Marcas (por artigos)</Typography>
            <Paper sx={{ p: 3 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell align="right">Artigos Total</TableCell>
                                <TableCell align="right">Artigos (30d)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stats?.topBrands?.map((brand, idx) => (
                                <TableRow key={brand._id}>
                                    <TableCell>{idx + 1}</TableCell>
                                    <TableCell>{brand.name}</TableCell>
                                    <TableCell align="right">
                                        <Chip label={brand.articleCount} color="primary" size="small" />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip label={brand.activeArticles} color="success" size="small" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Scraping Parallelism Config */}
            <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
                <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Configurações de Paralelismo de Scraping
            </Typography>

            <Grid container spacing={3}>
                {/* Config Card */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <SettingsIcon color="primary" />
                            <Typography variant="h6">Paralelismo</Typography>
                        </Box>

                        {scrapingConfigSuccess && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Configuração salva com sucesso!
                            </Alert>
                        )}

                        <Alert severity="info" sx={{ mb: 3 }}>
                            O paralelismo define quantas URLs o sistema processa simultaneamente durante o scraping.
                            Mais alto = mais rápido, mas maior risco de bloqueios pelos sites.
                        </Alert>

                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <FormLabel component="legend">
                                <Typography variant="body2" color="text.secondary">
                                    Paralelismo Atual: <strong>{scrapingConfig?.parallelLimit || 1} URL(s)</strong>
                                </Typography>
                            </FormLabel>
                            <Slider
                                value={scrapingConfig?.parallelLimit || 1}
                                onChange={(e, newValue) => setScrapingConfig(prev => ({ ...prev, parallelLimit: newValue }))}
                                min={1}
                                max={5}
                                step={1}
                                marks={[
                                    { value: 1, label: '1' },
                                    { value: 2, label: '2' },
                                    { value: 3, label: '3' },
                                    { value: 4, label: '4' },
                                    { value: 5, label: '5' }
                                ]}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(value) => `${value} URL${value > 1 ? 's' : ''}`}
                            />
                        </FormControl>

                        <Alert severity="warning" sx={{ mb: 2 }}>
                            ⚠️ Pool de navegadores: 2 instâncias. Valores acima de 2 podem causar timeouts em sites que exigem JavaScript.
                        </Alert>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveScrapingConfig}
                            disabled={scrapingConfigSaving}
                            startIcon={scrapingConfigSaving ? <CircularProgress size={20} /> : null}
                        >
                            {scrapingConfigSaving ? 'Salvando...' : '💾 Salvar Paralelismo'}
                        </Button>
                    </Paper>
                </Grid>

                {/* Speed Test Card */}
                <Grid item xs={12} md={6}>
                    <ScrapingSpeedTest onConfigChange={handleSpeedTestResult} />
                </Grid>
            </Grid>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 4, textAlign: 'center' }}>
                Última atualização: {stats?.timestamp ? new Date(stats.timestamp).toLocaleString() : 'N/A'}
            </Typography>
        </Container>
        </AdminLayout>
    );
};

export default SystemDashboardPage;
